import { definedNotNull } from '@df/common';
import { get, writable } from 'svelte/store';
import { appUrl } from './config.js';
import { json } from './json';
import { z } from 'zod';
/**
 * Every localStorage key this app owns, and whether it outlives a logout.
 * Display preferences do; anything naming a user, a customer or a credential
 * does not. A key absent from here — a third party's, such as MSAL's token
 * cache — is cleared, so forgetting to classify one fails safe.
 */
const localStorageKeys = {
    config: 'survives-logout',
    devAccess: 'survives-logout',
    employer: 'clear-on-logout',
    microsoftAccount: 'clear-on-logout',
    user: 'clear-on-logout',
    guest: 'clear-on-logout',
    refreshToken: 'clear-on-logout',
    navigationHistory: 'clear-on-logout',
    sampling: 'clear-on-logout',
    toasts: 'clear-on-logout',
};
const keysSurvivingLogout = new Set(Object.entries(localStorageKeys)
    .filter(([, lifetime]) => lifetime === 'survives-logout')
    .map(([key]) => key));
const _sessionStorageKeys = ['sessionId', 'writeProtection'];
export function createLocalStorageStore(key, decoder, onError, initValue) {
    const store = writable(initValue);
    if (typeof window === 'undefined')
        return store;
    try {
        const storedValueString = window.localStorage.getItem(key);
        if (storedValueString !== null) {
            store.set(decoder(JSON.parse(storedValueString, json.reviver)));
        }
    }
    catch (error) {
        onError(error);
    }
    store.subscribe((val) => {
        try {
            if (!definedNotNull(val)) {
                window.localStorage.removeItem(key);
            }
            else {
                window.localStorage.setItem(key, JSON.stringify(val, json.replacer));
            }
        }
        catch (error) {
            onError(error);
        }
    });
    window.addEventListener('storage', () => {
        try {
            const storedValueString = window.localStorage.getItem(key);
            if (storedValueString === null)
                return;
            const localStorageValue = decoder(JSON.parse(storedValueString, json.reviver));
            if (localStorageValue !== get(store))
                store.set(localStorageValue);
        }
        catch (error) {
            onError(error);
        }
    });
    return store;
}
export function createLocalStorageStoreWithZod(key, schema, options = {}) {
    const store = writable(options.initValue);
    if (typeof window === 'undefined')
        return store;
    const handleError = (error) => {
        if (options.onError)
            options.onError(error);
        else
            resetStoresAndLogout(key, error);
    };
    try {
        const storedValueString = window.localStorage.getItem(key);
        if (storedValueString !== null) {
            store.set(schema.parse(JSON.parse(storedValueString, json.reviver)));
        }
    }
    catch (error) {
        handleError(error);
    }
    store.subscribe((val) => {
        try {
            if (!definedNotNull(val)) {
                window.localStorage.removeItem(key);
            }
            else {
                window.localStorage.setItem(key, JSON.stringify(val, json.replacer));
            }
        }
        catch (error) {
            handleError(error);
        }
    });
    window.addEventListener('storage', () => {
        try {
            const storedValueString = window.localStorage.getItem(key);
            if (storedValueString === null)
                return;
            const localStorageValue = schema.parse(JSON.parse(storedValueString, json.reviver));
            if (localStorageValue !== get(store))
                store.set(localStorageValue);
        }
        catch (error) {
            handleError(error);
        }
    });
    return store;
}
export function createSessionStorageStore(key, decoder, onError, initValue) {
    const store = writable(initValue);
    if (typeof window === 'undefined')
        return store;
    try {
        const storedValueString = window.sessionStorage.getItem(key);
        if (storedValueString !== null)
            store.set(decoder(JSON.parse(storedValueString)));
    }
    catch (error) {
        onError(error);
    }
    store.subscribe((val) => {
        try {
            if (!definedNotNull(val)) {
                window.sessionStorage.removeItem(key);
            }
            else {
                window.sessionStorage.setItem(key, JSON.stringify(val));
            }
        }
        catch (error) {
            onError(error);
        }
    });
    return store;
}
/**
 * Recovery from a value that would not decode. Takes everything, including the
 * keys a logout spares — the offending one may be among them, and sparing it
 * would fail the same way on the next load.
 */
export function resetStoresAndLogout(key, error) {
    console.error(key, error);
    localStorage.clear();
    location.href = `${appUrl}`;
}
export function clearLocalStorage() {
    for (const key of Object.keys(localStorage)) {
        if (!keysSurvivingLogout.has(key)) {
            localStorage.removeItem(key);
        }
    }
}
/** Nothing in sessionStorage outlives a logout: both keys are session state. */
export function clearSessionStorage() {
    sessionStorage.clear();
}
export function clearStorage() {
    clearLocalStorage();
    clearSessionStorage();
}
